<template>
  <div>
    <!-- MODO NORMAL: exibe botão para redirecionar à rota de impressão -->
    <div v-if="!printMode">
      <h6>{{ props.printTitle }}</h6><p></p>
      <button @click="abrirPrint" class="btn btn-primary mb-3">
        Executar
      </button>
    </div>

    <!-- MODO IMPRESSÃO: busca contagem e lista e exibe tudo -->
    <div v-else>
      <!-- Spinner geral enquanto carrega ou contagem ou alunos -->
      <div v-if="loadingContagem || loadingAlunos" class="mb-3">
        Carregando…
      </div>

      <div v-else>
        <!-- QUADRO DE RESUMO -->
        <table class="table table-bordered mb-4" style="width: auto;">
          <thead>
            <tr class="text-center">
              <th>Ativa</th>
              <th>Trancada</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr class="text-center">
              <td>{{ counts.ativa }}</td>
              <td>{{ counts.trancada }}</td>
              <td>{{ counts.total }}</td>
            </tr>
          </tbody>
        </table>

        <!-- TABELA DE Matrículas Ativas (com status) -->
        <div v-if="!loadingAlunos && resultados.length">
          <table class="table table-striped">
            <thead>
              <tr>
                <th>MAT</th>
                <th>NOME</th>
                <th>E-MAIL</th>
                <th>CELULAR</th>
                <th>RESPONSÁVEL</th>
                <th>STATUS</th>
                <th class="text-center">INÍCIO</th>
                <th class="text-center">TÉRMINO</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="aluno in resultados"
                :key="aluno.matricula_id"
                :class="{ 'table-secondary': aluno.status === 'Trancada' }"
              >
                <td>{{ aluno.matricula_id }}</td>
                <td>{{ aluno.nome }}</td>
                <td>{{ aluno.email || '' }}</td>
                <td>{{ aluno.celular || '' }}</td>
                <td>{{ aluno.responsavel || '' }}</td>
                <td>{{ aluno.status }}</td>
                <td class="text-center">{{ formatarData(aluno.inicio_vigencia) }}</td>
                <td class="text-center">{{ formatarData(aluno.termino_vigencia) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Caso não haja nenhum registro -->
        <div v-else-if="!loadingAlunos && executedAlunos">
          <p>Não há Matrículas Ativas (ou suspensos) neste momento.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'

// --- PROPS vindas da rota de impressão ---
const props = defineProps({
  printMode:  { type: Boolean, default: false },
  printTitle: { type: String,  default: 'Lista de Matrículas Ativas' }
})

const router = useRouter()

// --- Estados para contagem e carregamento de alunos ---
const counts           = ref({ ativa: 0, trancada: 0, total: 0 })
const loadingContagem  = ref(false)

const loadingAlunos   = ref(false)
const resultados      = ref([])
const executedAlunos  = ref(false)

/**
 * Busca as contagens (ativa, trancada, total) considerando apenas
 * matrículas que estão na vigência atual (ativo por término ≥ hoje ou NULL).
 */
async function buscarContagem() {
  loadingContagem.value = true
  try {
    const res = await axios.get('/gestao/relatorios/cadastrais/lista-alunos-ativos/contagem-matriculas')
    counts.value = {
      ativa:    res.data.ativa    ?? 0,
      trancada: res.data.trancada ?? 0,
      total:    res.data.total    ?? 0
    }
  } catch (err) {
    console.error('Erro ao buscar contagem de matrículas:', err)
    // counts permanecerá {0,0,0} em caso de erro
  } finally {
    loadingContagem.value = false
  }
}

/**
 * Busca a lista de alunos “ativos” (vigência válida), retornando
 * também o campo “status” que aponta se está trancado ou não.
 */
async function buscarAlunosAtivos() {
  loadingAlunos.value   = true
  executedAlunos.value  = false
  resultados.value      = []

  try {
    const res = await axios.get('/gestao/relatorios/cadastrais/lista-alunos-ativos')
    resultados.value = Array.isArray(res.data) ? res.data : []
  } catch (err) {
    console.error('Erro ao buscar Matrículas Ativas:', err)
    resultados.value = []
  } finally {
    loadingAlunos.value   = false
    executedAlunos.value  = true
  }
}

onMounted(() => {
  if (!props.printMode) return

  // MODO IMPRESSÃO: dispara as duas buscas em paralelo
  buscarContagem()
  buscarAlunosAtivos()
})

function abrirPrint() {
  router.push({
    path: `/gestao/relatorios/print/alunos-ativos`,
    query: { title: props.printTitle }
  })
}

function formatarData(dateStr) {
  if (!dateStr) return ''
  const d   = new Date(dateStr)
  const dia = String(d.getDate()).padStart(2, '0')
  const mes = String(d.getMonth() + 1).padStart(2, '0')
  const ano = d.getFullYear()
  return `${dia}/${mes}/${ano}`
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 1rem;
}

/* Destacar em cinza claro as linhas “Trancadas” */
.table-secondary {
  background-color: #f8f9fa !important;
}

/* Ajuste de largura para o quadro de resumo */
.table-bordered {
  width: auto;
  margin-bottom: 1rem;
}

.text-center {
  text-align: center;
}
</style>
